import React from "react";
import { EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtNum } from "@/lib/format";
import { BellRing, Trash2, TrendingUp, TrendingDown, Repeat } from "lucide-react";

const describe = (a, t) => {
  const isAbove = a.direction === "above";
  const atype = a.alert_type || "price";
  const sign = isAbove ? "+" : "-";
  if (atype === "price") {
    return {
      head: t(isAbove ? "alr_rises_above" : "alr_falls_below"),
      value: fmtNum(a.target_price),
      sub: a.current_price != null ? `${t("alr_current")}: ${fmtNum(a.current_price)}` : null,
    };
  }
  if (atype === "percent_move") {
    return {
      head: t("alr_type_percent_move"),
      value: `${sign}${a.percent}%`,
      sub: a.current_move_pct != null ? `${t("alr_current")}: ${a.current_move_pct}%` : null,
    };
  }
  return {
    head: t("alr_type_daily_change"),
    value: `${sign}${a.percent}%`,
    sub: a.current_change_pct != null ? `${t("alr_current")}: ${a.current_change_pct}%` : null,
  };
};

const AlertRow = ({ a, t, onDelete }) => {
  const isAbove = a.direction === "above";
  const triggered = a.status === "triggered";
  const { head, value, sub } = describe(a, t);
  return (
    <div className="rounded-lg border p-3 flex items-center gap-3" data-testid={`alert-item-${a.id}`}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${isAbove ? "bg-emerald-500/15" : "bg-red-500/15"}`}>
        {isAbove ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{a.symbol}</span>
          <Badge variant={triggered ? "default" : "secondary"} className="text-[10px]">
            {triggered ? t("triggered") : t("active")}
          </Badge>
          {a.recurring && (
            <Badge variant="outline" className="text-[10px] gap-0.5" data-testid={`alert-recurring-${a.id}`}>
              <Repeat className="h-3 w-3" />{a.trigger_count > 0 ? `×${a.trigger_count}` : ""}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">{a.name}</div>
      </div>
      <div className="text-end">
        <div className="text-xs text-muted-foreground">{head}</div>
        <div className="numeric font-semibold text-sm">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground numeric">{sub}</div>}
      </div>
      <Button variant="ghost" size="icon" onClick={() => onDelete(a.id)} data-testid={`alert-delete-${a.id}`}>
        <Trash2 className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  );
};

export const AlertsActiveList = ({ alerts, loading, t, onDelete }) => {
  if (loading) return <EmptyState title="..." />;
  if (alerts.length === 0) {
    return <EmptyState title={t("alr_no_alerts")} hint={t("alr_no_alerts_hint")} testid="alerts-empty" />;
  }
  return (
    <div className="space-y-2.5" data-testid="alerts-list">
      {alerts.map((a) => <AlertRow key={a.id} a={a} t={t} onDelete={onDelete} />)}
    </div>
  );
};
